import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'usehooks-ts';
import useSession from '@/hooks/use-session';
import { isAgentsTabAccessible } from '@/utils/agents-access';
import type { ResourceContext } from '../types';
import { type ArrivalOrigin, type OpenOutcome, subscribeResourceOpen } from '../utils/resource-arrival-bus';
import { presentResource } from '../utils/resource-presentation';
import useShowChatBeside from './use-show-chat-beside';
type OpenBlocker = Extract<OpenOutcome, 'other_environment' | 'leaves_chat'>;

// Where a page Percy opens lives, and what stops it from moving the page. Pages load with the user's
// own session, so this guards against stranding them, not access: the dashboard has to be in the
// chat's environment already (switching it can log them out), and the page has to keep the chat,
// which the agent builder hides without the Agents tab.
const useOpenTarget = () => {
  const $ = _c(4);
  const {
    t
  } = useTranslation("copilot");
  const {
    isLive,
    data: t0
  } = useSession();
  const {
    user
  } = t0;
  let t1;
  if ($[0] !== isLive || $[1] !== t || $[2] !== user) {
    t1 = (resource, chat) => {
      const href = presentResource(resource, t, {
        chatSessionId: chat.chatSessionId
      })?.href;
      if (!href) {
        return null;
      }
      const keepsChat = resource.kind !== "agent" || isAgentsTabAccessible({
        user
      });
      const blocker = chat.isLive !== undefined && chat.isLive !== isLive ? "other_environment" : keepsChat ? null : "leaves_chat";
      return {
        href,
        blocker
      };
    };
    $[0] = isLive;
    $[1] = t;
    $[2] = user;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const useFollowPercyOpens = () => {
  const $ = _c(7);
  const router = useRouter();
  const openTarget = useOpenTarget();
  const showChatBeside = useShowChatBeside();
  let t0;
  if ($[0] !== openTarget || $[1] !== router || $[2] !== showChatBeside) {
    t0 = async (resource, chat, answer) => {
      const target = openTarget(resource, chat);
      if (!target) {
        return;
      }
      if (target.blocker) {
        answer(target.blocker);
        return;
      }
      showChatBeside();
      const moved = await router.push(target.href).catch(_temp);
      answer(moved ? "opened" : "navigation_failed");
    };
    $[0] = openTarget;
    $[1] = router;
    $[2] = showChatBeside;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const follow = useEventCallback(t0);
  let t1;
  let t2;
  if ($[4] !== follow) {
    t1 = () => subscribeResourceOpen(follow);
    t2 = [follow];
    $[4] = follow;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
};
export { useOpenTarget };
export default useFollowPercyOpens;
function _temp() {
  return false;
}